import type { LiveKind } from '@api/live';
import type {
  LiveMediaMode,
  MediaLiveKind,
} from '@base/lib/live-media/domain';
import { isMediaLiveKind as isMediaLiveKindValue } from '@base/lib/live-media/domain';

export const isMediaLiveKind = (
  kind: LiveKind | undefined
): kind is MediaLiveKind => isMediaLiveKindValue(kind);

export const getLiveKindLabel = (kind: LiveKind) => {
  switch (kind) {
    case 'audio':
      return '음성';
    case 'video':
      return '영상';
    default:
      return '채팅';
  }
};

export const getLiveMediaConstraints = (
  kind: MediaLiveKind,
  mode: LiveMediaMode = 'original'
): MediaStreamConstraints => ({
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  video:
    kind === 'video'
      ? {
          width: { ideal: mode === 'character' ? 640 : 1280 },
          height: { ideal: mode === 'character' ? 480 : 720 },
          frameRate:
            mode === 'character'
              ? { ideal: 15, max: 15 }
              : { ideal: 24, max: 30 },
          facingMode: 'user',
        }
      : false,
});

export const prepareLiveMediaStream = async (
  kind: MediaLiveKind,
  mode: LiveMediaMode = 'original'
) => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException(
      '이 환경에서는 카메라와 마이크를 사용할 수 없습니다.',
      'NotSupportedError'
    );
  }
  return navigator.mediaDevices.getUserMedia(
    getLiveMediaConstraints(kind, mode)
  );
};

export const stopLiveMediaStream = (stream?: MediaStream | null) => {
  stream?.getTracks().forEach((track) => track.stop());
};

export const getLiveMediaDeviceErrorMessage = (
  error: unknown,
  target: 'media' | 'microphone' = 'media'
) => {
  const name = error instanceof DOMException ? error.name : '';
  const deviceLabel = target === 'microphone' ? '마이크' : '카메라와 마이크';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return `${deviceLabel} 권한을 허용해주세요.`;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return target === 'microphone'
        ? '사용할 수 있는 마이크를 찾지 못했어요.'
        : '사용할 수 있는 카메라 또는 마이크를 찾지 못했어요.';
    case 'NotReadableError':
    case 'TrackStartError':
      return `다른 앱에서 ${deviceLabel}를 사용 중인지 확인해주세요.`;
    case 'NotSupportedError':
      return target === 'microphone'
        ? '현재 환경에서는 음성 미리듣기를 사용할 수 없어요.'
        : '현재 환경에서는 음성·영상 라이브를 사용할 수 없어요.';
    default:
      return target === 'microphone'
        ? '음성 미리듣기를 시작하지 못했어요. 다시 시도해주세요.'
        : '카메라와 마이크를 준비하지 못했어요. 다시 시도해주세요.';
  }
};
